import { useApiData } from "@/hooks/useApiData";
import type { ContactSubmission } from "@shared/types";
import { Trash2, Mail, Eye, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";

const statusLabels: Record<string, { label: string; color: string; icon: typeof Mail }> = {
  new: { label: "New", color: "bg-blue-500/20 text-blue-400", icon: AlertCircle },
  read: { label: "Read", color: "bg-yellow-500/20 text-yellow-400", icon: Eye },
  replied: { label: "Replied", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
};

export default function ContactSubmissions() {
  const { data: submissions, refetch } = useApiData<ContactSubmission[]>("/contact-submissions");
  const [deleting, setDeleting] = useState<number | null>(null);

  const updateStatus = async (id: number, status: string) => {
    await api.put(`/contact-submissions/${id}/status`, { status });
    refetch();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this submission?")) return;
    setDeleting(id);
    await api.delete(`/contact-submissions/${id}`);
    refetch();
    setDeleting(null);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + "Z");
    return d.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const items = Array.isArray(submissions) ? submissions : [];
  const newCount = items.filter((s) => s.status === "new").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white font-[Rubik]">Leads</h1>
          {newCount > 0 && (
            <span className="bg-blue-500/20 text-blue-400 text-xs font-bold px-2.5 py-1 rounded-full">
              {newCount} new
            </span>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No submissions yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((sub) => {
            const statusInfo = statusLabels[sub.status] || statusLabels.new;
            const StatusIcon = statusInfo.icon;

            return (
              <div
                key={sub.id}
                className={`glass-card p-5 border ${
                  sub.status === "new" ? "border-blue-500/20" : "border-white/5"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-medium">{sub.name}</span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusInfo.label}
                      </span>
                      {sub.crmSynced && (
                        <span className="text-xs text-green-500/60" title="Synced to CRM">
                          CRM ✓
                        </span>
                      )}
                      <span className="text-xs text-white/30 capitalize">{sub.source}</span>
                    </div>

                    {(sub.utmSource || sub.utmMedium || sub.utmCampaign) && (
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {sub.utmSource && (
                          <span className="text-xs bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded">
                            src: {sub.utmSource}
                          </span>
                        )}
                        {sub.utmMedium && (
                          <span className="text-xs bg-indigo-500/10 text-indigo-400 px-1.5 py-0.5 rounded">
                            med: {sub.utmMedium}
                          </span>
                        )}
                        {sub.utmCampaign && (
                          <span className="text-xs bg-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded">
                            cmp: {sub.utmCampaign}
                          </span>
                        )}
                        {sub.utmTerm && (
                          <span className="text-xs bg-white/5 text-white/40 px-1.5 py-0.5 rounded">
                            term: {sub.utmTerm}
                          </span>
                        )}
                        {sub.utmContent && (
                          <span className="text-xs bg-white/5 text-white/40 px-1.5 py-0.5 rounded">
                            content: {sub.utmContent}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm text-white/50 mb-2">
                      <a href={`tel:${sub.phone}`} className="hover:text-[#66CDAA] transition-colors" dir="ltr">
                        {sub.phone}
                      </a>
                      <a href={`mailto:${sub.email}`} className="hover:text-[#66CDAA] transition-colors">
                        {sub.email}
                      </a>
                    </div>

                    {sub.message && (
                      <p className="text-sm text-white/40 leading-relaxed">{sub.message}</p>
                    )}

                    <div className="flex items-center gap-2 mt-3 text-xs text-white/30">
                      <Clock className="w-3 h-3" />
                      {formatDate(sub.createdAt)}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {sub.status === "new" && (
                      <button
                        onClick={() => updateStatus(sub.id, "read")}
                        className="p-2 text-white/30 hover:text-yellow-400 transition-colors"
                        title="Mark as read"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {sub.status !== "replied" && (
                      <button
                        onClick={() => updateStatus(sub.id, "replied")}
                        className="p-2 text-white/30 hover:text-green-400 transition-colors"
                        title="Mark as replied"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(sub.id)}
                      disabled={deleting === sub.id}
                      className="p-2 text-white/30 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
